import { contractTypeLabel } from "@etudescarre/constants";
import {
  Button,
  DataTable,
  type DataTableColumn,
  Modal,
  Select,
  type SelectOption,
} from "@etudescarre/ui";
import { isNotNil } from "@etudescarre/utils";
import { ArrowLeftIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";

type ContractBatiment = {
  id: string;
  buildingId: number;
  building: { id: number; name: string };
};

type ContractDetail = {
  id: number;
  yardId: number;
  customerId: number | null;
  performerId: number;
  groupId: number | null;
  type: string;
  shortName: string | null;
  isActive: boolean;
  isLead: boolean;
  delayForNoticeDays: number | null;
  createdAt: string;
  yard: { id: number; name: string };
  performer: { id: number; name: string };
  customer: { id: number; name: string } | null;
  group: { id: number; name: string } | null;
  contractBatiments: ContractBatiment[];
};

export function ContractDetailPage() {
  const { contractId } = useParams<{ contractId: string }>();
  const navigate = useNavigate();
  const [contract, setContract] = useState<ContractDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingBatimentId, setDeletingBatimentId] = useState<string | null>(
    null,
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [buildingOptions, setBuildingOptions] = useState<SelectOption[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(
    null,
  );
  const [isAdding, setIsAdding] = useState(false);

  const numericContractId = Number(contractId);

  const fetchContract = useCallback(async () => {
    setLoading(true);
    const { data, error } = await apiClient.GET("/api/admin/contracts/{id}", {
      params: { path: { id: numericContractId } },
    });
    if (error) {
      setLoading(false);
      return;
    }
    setContract({
      ...data.data.contract,
      contractBatiments: data.data.contract.contractBatiments.map((cb) => ({
        ...cb,
        id: String(cb.id),
      })),
    });
    setLoading(false);
  }, [numericContractId]);

  const fetchBuildings = useCallback(
    async (yardId: number) => {
      const { data } = await apiClient.GET("/api/admin/yards/buildings", {
        params: { query: { yardId } },
      });
      if (data) {
        // Filter out buildings already linked
        const linkedIds = new Set(
          contract?.contractBatiments.map((cb) => cb.buildingId) ?? [],
        );
        setBuildingOptions(
          data.data.buildings
            .filter((b) => !linkedIds.has(b.id))
            .map((b) => ({ label: b.name, value: String(b.id) })),
        );
      }
    },
    [contract],
  );

  useEffect(() => {
    fetchContract();
  }, [fetchContract]);

  const handleOpenAddModal = () => {
    if (contract) {
      fetchBuildings(contract.yardId);
    }
    setSelectedBuildingId(null);
    setAddModalOpen(true);
  };

  const handleAddBatiment = async () => {
    if (!selectedBuildingId || !contract) return;

    setIsAdding(true);
    const { error } = await apiClient.POST(
      "/api/admin/contracts/{id}/batiments",
      {
        params: { path: { id: contract.id } },
        body: { buildingId: selectedBuildingId },
      },
    );

    if (error) {
      toaster.error({
        title: "Erreur",
        description: error.error || "Échec de l'ajout du bâtiment",
      });
      setIsAdding(false);
      return;
    }

    toaster.success({
      title: "Succès",
      description: "Bâtiment ajouté avec succès",
    });
    setAddModalOpen(false);
    setSelectedBuildingId(null);
    setIsAdding(false);
    fetchContract();
  };

  const handleDeleteBatiment = (batimentId: string) => {
    setDeletingBatimentId(batimentId);
    setDeleteModalOpen(true);
  };

  const confirmDeleteBatiment = async () => {
    if (!deletingBatimentId || !contract) return;

    setIsDeleting(true);
    const { error } = await apiClient.DELETE(
      "/api/admin/contracts/{id}/batiments/{batimentId}",
      {
        params: {
          path: {
            id: contract.id,
            batimentId: Number(deletingBatimentId),
          },
        },
      },
    );

    if (error) {
      toaster.error({
        title: "Erreur",
        description:
          error.error || "Échec de la suppression de l'association bâtiment",
      });
      setIsDeleting(false);
      return;
    }

    toaster.success({
      title: "Succès",
      description: "Association bâtiment supprimée",
    });
    setDeleteModalOpen(false);
    setDeletingBatimentId(null);
    setIsDeleting(false);
    fetchContract();
  };

  const batimentColumns: DataTableColumn<ContractBatiment>[] = [
    {
      id: "building",
      header: "Bâtiment",
      accessor: (row) => row.building.name,
      isSortable: true,
      loadingCell: "text",
    },
    {
      id: "actions",
      header: "Actions",
      accessor: () => "",
      cell: ({ row }) => (
        <Button
          plain
          onClick={() => handleDeleteBatiment(row.id)}
          aria-label="Retirer"
        >
          <TrashIcon className="size-4" />
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex min-h-[200px] items-center justify-center text-gray-500">
          Chargement...
        </div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex min-h-[200px] items-center justify-center text-gray-500">
          Contrat introuvable
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6 flex items-center gap-4">
        <Button
          plain
          onClick={() => navigate("/admin/contracts")}
          aria-label="Retour"
        >
          <ArrowLeftIcon className="size-5" />
        </Button>
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
          Contrat — {contractTypeLabel(contract.type)}
          {contract.shortName ? ` (${contract.shortName})` : ""}
        </h2>
      </div>

      {/* Contract Details Card */}
      <div className="mb-8 rounded-lg border border-zinc-200 bg-white p-6 dark:border-zinc-800 dark:bg-zinc-900">
        <h3 className="mb-4 text-lg font-medium text-zinc-900 dark:text-zinc-100">
          Informations
        </h3>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
              Chantier
            </dt>
            <dd className="mt-1 text-sm text-zinc-900 dark:text-zinc-100">
              {contract.yard.name}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
              Type
            </dt>
            <dd className="mt-1 text-sm text-zinc-900 dark:text-zinc-100">
              {contractTypeLabel(contract.type)}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
              Entreprise intervenante
            </dt>
            <dd className="mt-1 text-sm text-zinc-900 dark:text-zinc-100">
              {contract.performer.name}
            </dd>
          </div>
          {contract.customer && (
            <div>
              <dt className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
                Maître d'ouvrage
              </dt>
              <dd className="mt-1 text-sm text-zinc-900 dark:text-zinc-100">
                {contract.customer.name}
              </dd>
            </div>
          )}
          {contract.group && (
            <div>
              <dt className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
                Groupe
              </dt>
              <dd className="mt-1 text-sm text-zinc-900 dark:text-zinc-100">
                {contract.group.name}
              </dd>
            </div>
          )}
          {contract.shortName && (
            <div>
              <dt className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
                Code contrat
              </dt>
              <dd className="mt-1 text-sm text-zinc-900 dark:text-zinc-100">
                {contract.shortName}
              </dd>
            </div>
          )}
          <div>
            <dt className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
              Statut
            </dt>
            <dd className="mt-1 text-sm text-zinc-900 dark:text-zinc-100">
              {contract.isActive ? "Actif" : "Inactif"}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
              Mandataire
            </dt>
            <dd className="mt-1 text-sm text-zinc-900 dark:text-zinc-100">
              {contract.isLead ? "Oui" : "Non"}
            </dd>
          </div>
          {isNotNil(contract.delayForNoticeDays) && (
            <div>
              <dt className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
                Délai de notification
              </dt>
              <dd className="mt-1 text-sm text-zinc-900 dark:text-zinc-100">
                {contract.delayForNoticeDays} jour
                {contract.delayForNoticeDays !== 1 ? "s" : ""}
              </dd>
            </div>
          )}
        </dl>
      </div>

      {/* Batiments Section */}
      <div>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">
            Bâtiments associés
          </h3>
          <Button onClick={handleOpenAddModal}>
            <PlusIcon data-slot="icon" className="size-4" />
            Ajouter un bâtiment
          </Button>
        </div>

        <DataTable
          columns={batimentColumns}
          data={contract.contractBatiments}
          isLoading={false}
          isEmpty={contract.contractBatiments.length === 0}
          emptyState={
            <div className="flex min-h-[120px] items-center justify-center text-gray-500">
              Aucun bâtiment associé
            </div>
          }
        />
      </div>

      {/* Add batiment modal */}
      <Modal.Root
        open={addModalOpen}
        onOpenChange={(details) => setAddModalOpen(details.open)}
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content>
              <Modal.Header>
                <Modal.Title>Ajouter un bâtiment</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div className="space-y-3">
                  <p className="text-sm text-zinc-600 dark:text-zinc-400">
                    Sélectionnez un bâtiment du chantier à associer à ce
                    contrat.
                  </p>
                  <Select
                    options={buildingOptions}
                    value={
                      selectedBuildingId ? [String(selectedBuildingId)] : []
                    }
                    onValueChange={(details) => {
                      const val = details.value[0];
                      setSelectedBuildingId(val ? Number(val) : null);
                    }}
                    placeholder={
                      buildingOptions.length === 0
                        ? "Aucun bâtiment disponible"
                        : "Sélectionner un bâtiment"
                    }
                    disabled={buildingOptions.length === 0}
                  />
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  outline
                  onClick={() => setAddModalOpen(false)}
                  disabled={isAdding}
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleAddBatiment}
                  disabled={isAdding || !selectedBuildingId}
                >
                  {isAdding ? "Ajout..." : "Ajouter"}
                </Button>
              </Modal.Footer>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>

      {/* Delete batiment modal */}
      <Modal.Root
        open={deleteModalOpen}
        onOpenChange={(details) => setDeleteModalOpen(details.open)}
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content>
              <Modal.Header>
                <Modal.Title>Retirer le bâtiment</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Modal.Description>
                  Êtes-vous sûr de vouloir retirer ce bâtiment du contrat ?
                  Cette action est irréversible.
                </Modal.Description>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  outline
                  onClick={() => setDeleteModalOpen(false)}
                  disabled={isDeleting}
                >
                  Annuler
                </Button>
                <Button
                  color="red"
                  onClick={confirmDeleteBatiment}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Suppression..." : "Retirer"}
                </Button>
              </Modal.Footer>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>
    </div>
  );
}

export type ContractDetailPageProps = Record<string, never>;
